/**
 * Base error class for next-route-compose errors.
 */
export class RouteComposeError extends Error {
    constructor(
        message: string,
        public readonly code: string,
        public readonly statusCode: number = 500
    ) {
        super(message);
        this.name = "RouteComposeError";
    }

    /**
     * Convert error to a JSON Response.
     */
    toResponse(): Response {
        return Response.json(
            {
                error: {
                    code: this.code,
                    message: this.message,
                },
            },
            { status: this.statusCode }
        );
    }
}

/**
 * Error thrown when authentication fails.
 */
export class UnauthorizedError extends RouteComposeError {
    constructor(message = "Unauthorized") {
        super(message, "UNAUTHORIZED", 401);
        this.name = "UnauthorizedError";
    }
}

/**
 * Error thrown when access is forbidden.
 */
export class ForbiddenError extends RouteComposeError {
    constructor(message = "Forbidden") {
        super(message, "FORBIDDEN", 403);
        this.name = "ForbiddenError";
    }
}

/**
 * Error thrown when rate limit is exceeded.
 */
export class RateLimitError extends RouteComposeError {
    constructor(
        message = "Too many requests",
        public readonly retryAfter?: number
    ) {
        super(message, "RATE_LIMITED", 429);
        this.name = "RateLimitError";
    }

    override toResponse(): Response {
        const headers: HeadersInit = {};
        if (this.retryAfter !== undefined) {
            headers["Retry-After"] = String(this.retryAfter);
        }
        return Response.json(
            {
                error: {
                    code: this.code,
                    message: this.message,
                },
            },
            { status: this.statusCode, headers }
        );
    }
}

/**
 * Error thrown when validation fails.
 */
export class ValidationError extends RouteComposeError {
    constructor(
        message = "Validation failed",
        public readonly details?: unknown
    ) {
        super(message, "VALIDATION_ERROR", 400);
        this.name = "ValidationError";
    }

    override toResponse(): Response {
        return Response.json(
            {
                error: {
                    code: this.code,
                    message: this.message,
                    details: this.details,
                },
            },
            { status: this.statusCode }
        );
    }
}

/**
 * Error thrown for bad requests.
 */
export class BadRequestError extends RouteComposeError {
    constructor(message = "Bad request") {
        super(message, "BAD_REQUEST", 400);
        this.name = "BadRequestError";
    }
}

/**
 * Error thrown when a resource is not found.
 */
export class NotFoundError extends RouteComposeError {
    constructor(message = "Not found") {
        super(message, "NOT_FOUND", 404);
        this.name = "NotFoundError";
    }
}

/**
 * Default error handler that converts errors to JSON responses.
 */
export function defaultErrorHandler(error: unknown): Response {
    if (error instanceof RouteComposeError) {
        return error.toResponse();
    }

    console.error("[next-route-compose] Unhandled error:", error);

    return Response.json(
        {
            error: {
                code: "INTERNAL_ERROR",
                message: "Internal server error",
            },
        },
        { status: 500 }
    );
}
